// CalendarWeekBar.tsx
import { CalendarOutlined, DownOutlined, InfoCircleFilled, LeftOutlined, RightOutlined } from '@ant-design/icons';
import DropOutline from 'assets/drop-outline.svg?react';
import Drop from 'assets/drop.svg?react';
import dayjs, { Dayjs } from 'dayjs';
import useShouldAskForPeriod from 'hooks/useShouldAskForPeriod';
import useSymptomTrackingData from 'hooks/useSymptomTrackingData';
import { cn } from 'lib/utils';
import SymptomTrackingSurveyModal from 'pages/SymptomTrackingSurvey';
import posthog from 'posthog-js';
import React, { useState } from 'react';
import type { DayButtonProps, MonthCaptionProps } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Calendar } from 'components/ui/calendar';
import { Collapsible, CollapsibleContent } from 'components/ui/collapsible';

// Week-bar status dots. The first (semantic) class name of each is asserted on
// by the functional tests (`to_have_class(re.compile(r"dotRed*"))`), so keep it.
const DOT = 'mt-1 size-2 rounded-full';
const DOT_CLASSES = {
  dot: `dot ${DOT} bg-border`,
  dotRed: `dotRed ${DOT} bg-chart-severity-very-bad`,
  dotGreen: `dotGreen ${DOT} bg-inverse`,
  dotRedOutlined: `dotRedOutlined ${DOT} border border-destructive bg-border`,
  dotFertileHollow: `dotFertileHollow ${DOT} border-2 border-info bg-transparent`,
  dotOvulationHollow: `dotOvulationHollow ${DOT} border-2 border-inverse bg-transparent`,
  dotFertileFilled: `dotFertileFilled ${DOT} bg-info`,
  dotOvulationFilled: `dotOvulationFilled ${DOT} bg-inverse`,
};

// Calendar-cell markers (below the day number)
// A day number, one fixed box so the markers below line up
const DAY_NUMBER = 'flex size-6 items-center justify-center';

const CELL_MARKER = 'box-border size-1.5 rounded-full';
const CELL_MARKER_CLASSES = {
  filledOutDay: `${CELL_MARKER} bg-inverse`,
  filledOutDayFertileHollow: `${CELL_MARKER} border-2 border-info`,
  filledOutDayOvulationHollow: `${CELL_MARKER} border-2 border-inverse`,
  filledOutDayFertile: `${CELL_MARKER} bg-info`,
  filledOutDayOvulation: `${CELL_MARKER} bg-inverse`,
  noDataDay: 'size-1.5 bg-card',
};

// The month view is the library's `Calendar`, laid out as a grid of white
// squares on the page background (what the Ant Design calendar looked like):
// the cells touch, separated by a 2px gap in the page colour, and each one is
// filled by its day's content. A cell is a seventh of the calendar's width in
// both directions (container query units, the calendar root being the
// container), a definite size: `aspect-square` on a flex item gets stretched
// to the tallest cell of its row, and the collapsible measures its height
// before the content has settled.
const MONTH_VIEW_CLASS_NAMES = {
  root: 'w-full',
  month_caption: '',
  weekday: 'flex-1 pb-1 text-center text-2xs font-semibold uppercase text-subtle-foreground',
  week: 'flex w-full',
  day: 'group/day relative size-[calc(100cqw/7)] shrink-0 overflow-hidden border-2 border-solid border-background bg-card p-px text-center select-none',
  outside: '',
  today: '',
};

const CalendarWeekBar: React.FC<{
  isCalendarOpen: boolean;
  setIsCalendarOpen: (isOpen: boolean) => void;
  onFillCycles: () => void;
}> = ({ isCalendarOpen, setIsCalendarOpen, onFillCycles }) => {
  const { t } = useTranslation();

  const { isPeriodTrackingEnabled } = useShouldAskForPeriod();

  const { getSpecificDailyStatus, refresh, getPredictionForDay, getFertilityStatusForDay } = useSymptomTrackingData();
  const today = dayjs();

  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);
  const [surveyModalDate, setSurveyModalDate] = useState<Dayjs>(dayjs());

  const weekDays = Array.from({ length: 7 }).map((_, i) => {
    return today.add(i - 3, 'day');
  });

  const getFertilityDotClass = (fertilityStatus: { type: 'ovulation' | 'fertile'; hollow: boolean }) => {
    if (fertilityStatus.type === 'ovulation')
      return fertilityStatus.hollow ? DOT_CLASSES.dotOvulationHollow : DOT_CLASSES.dotOvulationFilled;
    return fertilityStatus.hollow ? DOT_CLASSES.dotFertileHollow : DOT_CLASSES.dotFertileFilled;
  };

  const renderFertilityCalendarDot = (
    fertilityStatus: { type: 'ovulation' | 'fertile'; hollow: boolean },
    testId?: string,
  ) => {
    let className: string;
    if (fertilityStatus.type === 'ovulation')
      className = fertilityStatus.hollow
        ? CELL_MARKER_CLASSES.filledOutDayOvulationHollow
        : CELL_MARKER_CLASSES.filledOutDayOvulation;
    else
      className = fertilityStatus.hollow
        ? CELL_MARKER_CLASSES.filledOutDayFertileHollow
        : CELL_MARKER_CLASSES.filledOutDayFertile;
    return <div className={className} data-testid={testId} />;
  };

  const computeDayStatus = (date: Dayjs) => {
    const prediction = getPredictionForDay(date);
    const isPeriodPredicted = prediction !== null;
    const fertilityStatus = getFertilityStatusForDay(date);

    if (date.isBefore(today, 'day')) {
      const status = getSpecificDailyStatus(date);
      if (status) {
        if (status.menstruation_amount > 0) return DOT_CLASSES.dotRed;
        if (fertilityStatus) return getFertilityDotClass(fertilityStatus);
        return DOT_CLASSES.dotGreen;
      }
      if (fertilityStatus) return getFertilityDotClass(fertilityStatus);
    } else if (date.isSame(today, 'day')) {
      const status = getSpecificDailyStatus(date);
      if (status) {
        if (status.menstruation_amount > 0) return DOT_CLASSES.dotRed;
        if (fertilityStatus) return getFertilityDotClass(fertilityStatus);
        return DOT_CLASSES.dotGreen;
      }
      if (isPeriodPredicted) return DOT_CLASSES.dotRedOutlined;
      if (fertilityStatus) return getFertilityDotClass(fertilityStatus);
    } else {
      if (isPeriodPredicted) return DOT_CLASSES.dotRedOutlined;
      if (fertilityStatus) return getFertilityDotClass(fertilityStatus);
    }

    return DOT_CLASSES.dot;
  };

  const previousMonth = () => {
    // note: we add 2 days to exclude any edge-effects with timezones
    setSelectedDate(selectedDate.startOf('month').subtract(1, 'month').add(2, 'days'));
  };
  const nextMonth = () => {
    // note: we add 2 days to exclude any edge-effects with timezones
    setSelectedDate(selectedDate.startOf('month').add(1, 'month').add(2, 'days'));
  };

  function renderMenstruationLevel(value: number) {
    return [...Array(value)].map((_, i) => <Drop key={i} fill="var(--chart-severity-very-bad)" className="size-2" />);
  }

  // Day numbers read at three levels, in the month view and the week strip
  // alike: a day of another month is faint, a day still to come is washed
  // out, a day that can be filled in is plain, and today is bold.
  function getDayNumberClassName(date: Dayjs, outsideDisplayedMonth = false) {
    if (outsideDisplayedMonth) return 'text-subtle-foreground/70';
    if (date.isSame(today, 'day')) return 'font-bold text-foreground';
    if (date.isAfter(today, 'day')) return 'text-muted-foreground/50';
    return 'text-foreground';
  }

  function renderPeriodPredictionIndicator(current: Dayjs) {
    const prediction = getPredictionForDay(current);

    if (!prediction) {
      const fertilityStatus = getFertilityStatusForDay(current);
      if (fertilityStatus)
        return renderFertilityCalendarDot(fertilityStatus, `tracking.fertility-dot-${current.format('YYYY-MM-DD')}`);
      return <div className={CELL_MARKER_CLASSES.noDataDay} />;
    }

    if (prediction.isInMostLikelyPeriod) {
      return <DropOutline height={7} width={7} />;
    }

    return <DropOutline height={7} width={7} style={{ opacity: 0.3 }} />;
  }

  const renderDateCell = (current: Dayjs, outsideDisplayedMonth: boolean) => {
    const dayData = getSpecificDailyStatus(current);
    const fertilityStatus = getFertilityStatusForDay(current);
    return (
      <div className="flex size-full flex-col items-center justify-center text-lg">
        <div className="relative top-1 mb-1 flex h-1 w-full flex-row-reverse pr-1 text-2xs text-subtle-foreground">
          {dayData && dayData.comment && <InfoCircleFilled />}
        </div>
        <div className={cn(DAY_NUMBER, getDayNumberClassName(current, outsideDisplayedMonth))}>{current.date()}</div>
        {dayData ? (
          <div className="flex flex-row">
            <div className="flex flex-row">
              {dayData.menstruation_amount === 0 ? (
                fertilityStatus ? (
                  renderFertilityCalendarDot(fertilityStatus, `tracking.filled-out-day-${current.format('YYYY-MM-DD')}`)
                ) : (
                  <div
                    className={CELL_MARKER_CLASSES.filledOutDay}
                    data-testid={`tracking.filled-out-day-${current.format('YYYY-MM-DD')}`}
                  />
                )
              ) : (
                renderMenstruationLevel(dayData.menstruation_amount)
              )}
            </div>
          </div>
        ) : (
          renderPeriodPredictionIndicator(current)
        )}
      </div>
    );
  };

  // A day of the month view: the cell's whole square is the button, and its
  // content is ours rather than the day number react-day-picker passes as
  // `children`. `title` is the local date, which the functional tests click on.
  const MonthDayButton = ({ day, modifiers, className, children, ...props }: DayButtonProps) => {
    // the formatted day number is replaced by our cell
    void children;
    const date = dayjs(day.date);
    return (
      <button
        {...props}
        title={date.format('YYYY-MM-DD')}
        data-day={date.format('YYYY-MM-DD')}
        className={cn('absolute inset-0 block cursor-pointer border-0 bg-transparent p-0 text-foreground', className)}
      >
        {renderDateCell(date, Boolean(modifiers.outside))}
      </button>
    );
  };

  // The month's header: the previous/next buttons around the month name. The
  // icons' labels ("left"/"right") are what the functional tests click on.
  const MonthCaption = ({ calendarMonth, displayIndex, className, ...props }: MonthCaptionProps) => (
    <div
      {...props}
      className={cn('flex items-center justify-between text-lg font-bold capitalize text-foreground', className)}
      data-display-index={displayIndex}
    >
      <Button variant="secondary" size="icon" onClick={previousMonth}>
        <LeftOutlined />
      </Button>
      {dayjs(calendarMonth.date).format('MMMM YYYY')}
      <Button variant="secondary" size="icon" onClick={nextMonth}>
        <RightOutlined />
      </Button>
    </div>
  );

  const onDateSelected = (date: Dayjs) => {
    setSelectedDate(date);
    if (date <= today) {
      setSurveyModalDate(date);
      setIsSurveyModalOpen(true);
    }
    posthog.capture('tracking.calendar_day_selected');
  };

  const toggleCalendar = () => {
    setIsCalendarOpen(!isCalendarOpen);
  };

  return (
    <div className="w-full">
      <Collapsible open={isCalendarOpen}>
        <CollapsibleContent>
          <div className="mb-6">
            <Calendar
              className="@container w-full bg-transparent p-0"
              classNames={MONTH_VIEW_CLASS_NAMES}
              components={{ DayButton: MonthDayButton, MonthCaption }}
              hideNavigation
              month={selectedDate.toDate()}
              onDayClick={date => onDateSelected(dayjs(date))}
            />
            <div className="my-3 flex flex-col items-center justify-center">
              {isPeriodTrackingEnabled && (
                <Button variant="secondary" size="lg" onClick={onFillCycles}>
                  <Drop height={16} width={16} fill="var(--chart-severity-very-bad)" />
                  {t('symptomTracking.cycles.fillCycles')}
                </Button>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
      <div className="flex w-full items-end justify-between py-2">
        {weekDays.map((day, idx) => (
          <div
            className="flex w-8 flex-col items-center"
            key={idx}
            onClick={() => {
              if (!day.isAfter(today, 'day')) {
                setSurveyModalDate(day);
                setIsSurveyModalOpen(true);
              }
            }}
          >
            <span className="mb-0.5 text-sm capitalize text-subtle-foreground">{day.format('dd').slice(0, 2)}</span>
            <span className={cn(DAY_NUMBER, 'text-lg font-normal', getDayNumberClassName(day))}>{day.date()}</span>
            <span className={computeDayStatus(day)} />
          </div>
        ))}
        <button
          className="flex w-8 cursor-pointer flex-col items-center gap-1 border-0 bg-transparent p-0 text-2xl text-foreground"
          onClick={toggleCalendar}
          aria-expanded={isCalendarOpen}
          aria-label="Toggle calendar view"
          type="button"
        >
          <CalendarOutlined />
          <DownOutlined style={{ fontSize: 15, transform: isCalendarOpen ? 'rotate(180deg)' : 'none' }} />
        </button>
      </div>
      <SymptomTrackingSurveyModal
        open={isSurveyModalOpen}
        key={surveyModalDate.format('YYYY-MM-DD')}
        onClose={() => {
          setIsSurveyModalOpen(false);
          refresh();
        }}
        date={surveyModalDate}
      />
    </div>
  );
};

export default CalendarWeekBar;
